import { useEffect, useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { Code2, Copy, Eye, Loader2, PlayCircle, Plus, RefreshCw, Repeat, Trash2, Webhook } from 'lucide-react'

interface OutboundWebhook {
  id: string
  organizationId: string
  name: string
  url: string
  secret: string
  events: string[]
  headers: Record<string, string>
  active: boolean
  lastDeliveredAt: string | null
  lastStatus: string | null
  createdAt: string
  updatedAt: string
}

interface Delivery {
  id: string
  event: string
  status: 'pending' | 'delivered' | 'failed'
  attempts: number
  responseCode: number | null
  responseBody: string | null
  error: string | null
  createdAt: string
  deliveredAt: string | null
}

interface Props {
  orgId: string
}

const DEFAULT_FORM = {
  name: '',
  url: '',
  events: [] as string[],
  active: true,
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function statusVariant(status: string | null | undefined): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'delivered') return 'default'
  if (status === 'failed') return 'destructive'
  if (status === 'retrying' || status === 'pending') return 'secondary'
  return 'outline'
}

export default function OutboundWebhooksSection({ orgId }: Props) {
  const { toast } = useToast()
  const [hooks, setHooks] = useState<OutboundWebhook[]>([])
  const [events, setEvents] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<OutboundWebhook | null>(null)
  const [form, setForm] = useState(DEFAULT_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deliveries, setDeliveries] = useState<Record<string, Delivery[]>>({})
  const [deliveriesLoading, setDeliveriesLoading] = useState<string | null>(null)
  const [testingId, setTestingId] = useState<string | null>(null)
  const [previewEvent, setPreviewEvent] = useState<string | null>(null)
  const [previewSample, setPreviewSample] = useState<Record<string, any> | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [replaying, setReplaying] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const [hooksRes, eventsRes] = await Promise.all([
        api.get(`/api/platform/organizations/${orgId}/webhooks`),
        api.get(`/api/platform/webhook-events`),
      ])
      setHooks(hooksRes.data.data || [])
      setEvents(eventsRes.data.data?.events || [])
    } catch (err: any) {
      toast({
        title: 'Failed to load webhooks',
        description: err?.response?.data?.error?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const openCreate = () => {
    setEditing(null)
    setForm(DEFAULT_FORM)
    setShowForm(true)
  }

  const openEdit = (hook: OutboundWebhook) => {
    setEditing(hook)
    setForm({ name: hook.name, url: hook.url, events: hook.events, active: hook.active })
    setShowForm(true)
  }

  const submit = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name required', variant: 'destructive' })
      return
    }
    if (!form.url.trim()) {
      toast({ title: 'URL required', variant: 'destructive' })
      return
    }
    if (!form.events.length) {
      toast({ title: 'Select at least one event', variant: 'destructive' })
      return
    }
    setSubmitting(true)
    try {
      if (editing) {
        await api.put(`/api/platform/organizations/${orgId}/webhooks/${editing.id}`, form)
        toast({ title: 'Webhook updated' })
      } else {
        await api.post(`/api/platform/organizations/${orgId}/webhooks`, form)
        toast({ title: 'Webhook created' })
      }
      setShowForm(false)
      await load()
    } catch (err: any) {
      toast({
        title: 'Save failed',
        description: err?.response?.data?.error?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const toggleActive = async (hook: OutboundWebhook) => {
    try {
      await api.put(`/api/platform/organizations/${orgId}/webhooks/${hook.id}`, { active: !hook.active })
      await load()
    } catch (err: any) {
      toast({ title: 'Toggle failed', description: err?.message, variant: 'destructive' })
    }
  }

  const remove = async (hook: OutboundWebhook) => {
    if (!confirm(`Delete webhook "${hook.name}"? This cannot be undone.`)) return
    try {
      await api.delete(`/api/platform/organizations/${orgId}/webhooks/${hook.id}`)
      toast({ title: 'Webhook deleted' })
      await load()
    } catch (err: any) {
      toast({ title: 'Delete failed', description: err?.message, variant: 'destructive' })
    }
  }

  const rotate = async (hook: OutboundWebhook) => {
    if (!confirm(`Rotate signing secret for "${hook.name}"? The current secret will stop working immediately.`)) return
    try {
      const res = await api.post(`/api/platform/organizations/${orgId}/webhooks/${hook.id}/rotate-secret`)
      toast({ title: 'Secret rotated', description: 'Update the receiving system with the new secret.' })
      setHooks(prev => prev.map(h => (h.id === hook.id ? res.data.data : h)))
    } catch (err: any) {
      toast({ title: 'Rotate failed', description: err?.message, variant: 'destructive' })
    }
  }

  const testFire = async (hook: OutboundWebhook, event: string) => {
    setTestingId(hook.id)
    try {
      const res = await api.post(
        `/api/platform/organizations/${orgId}/webhooks/${hook.id}/test`,
        { event }
      )
      const delivery: Delivery = res.data.data
      const ok = delivery.status === 'delivered'
      toast({
        title: ok ? `Sent ${event}` : `Failed: ${event}`,
        description: ok
          ? `HTTP ${delivery.responseCode}`
          : delivery.error || `HTTP ${delivery.responseCode || '?'}`,
        variant: ok ? 'default' : 'destructive',
      })
      if (expandedId === hook.id) await loadDeliveries(hook.id)
      await load()
    } catch (err: any) {
      toast({
        title: 'Test failed',
        description: err?.response?.data?.error?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setTestingId(null)
    }
  }

  const replayLatestLead = async () => {
    setReplaying(true)
    try {
      const res = await api.post(`/api/platform/organizations/${orgId}/replay-latest-lead`)
      const { name, phone, leadId } = res.data.data
      toast({
        title: 'Lead replayed',
        description: `Fired ${name || phone || leadId} as lead.created — check n8n.`,
      })
      // Refresh deliveries view if any webhook is currently expanded
      if (expandedId) await loadDeliveries(expandedId)
      await load()
    } catch (err: any) {
      toast({
        title: 'Replay failed',
        description: err?.response?.data?.error?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setReplaying(false)
    }
  }

  const openPreview = async (event: string) => {
    setPreviewEvent(event)
    setPreviewSample(null)
    setPreviewLoading(true)
    try {
      const res = await api.get(`/api/platform/webhook-events/${event}/sample`)
      setPreviewSample(res.data.data?.sample || null)
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err?.message, variant: 'destructive' })
    } finally {
      setPreviewLoading(false)
    }
  }

  const loadDeliveries = async (hookId: string) => {
    setDeliveriesLoading(hookId)
    try {
      const res = await api.get(`/api/platform/organizations/${orgId}/webhooks/${hookId}/deliveries`)
      setDeliveries(prev => ({ ...prev, [hookId]: res.data.data || [] }))
    } catch (err: any) {
      toast({ title: 'Failed to load deliveries', description: err?.message, variant: 'destructive' })
    } finally {
      setDeliveriesLoading(null)
    }
  }

  const toggleExpand = async (hookId: string) => {
    if (expandedId === hookId) {
      setExpandedId(null)
      return
    }
    setExpandedId(hookId)
    if (!deliveries[hookId]) await loadDeliveries(hookId)
  }

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: `${label} copied` })
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' })
    }
  }

  const toggleEvent = (event: string) => {
    setForm(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event],
    }))
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Webhook className="h-5 w-5" />
              Outbound Webhooks
            </CardTitle>
            <CardDescription>
              Send structured event data to Make.com, n8n, Zapier, or any custom HTTPS endpoint.
              Toggle events on per webhook — payloads are HMAC-SHA256 signed and retried on failure.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={replayLatestLead} size="sm" variant="outline" disabled={replaying}>
              {replaying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Repeat className="h-4 w-4 mr-1" />}
              Replay latest lead
            </Button>
            <Button onClick={openCreate} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add webhook
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading webhooks…
          </div>
        ) : hooks.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No outbound webhooks configured yet. Add one to start streaming events.
          </div>
        ) : (
          hooks.map(hook => (
            <div key={hook.id} className="border rounded-lg overflow-hidden">
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{hook.name}</span>
                    <Badge variant={statusVariant(hook.lastStatus)}>
                      {hook.lastStatus || 'never delivered'}
                    </Badge>
                    {!hook.active && <Badge variant="outline">disabled</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono mt-1 truncate">{hook.url}</div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {hook.events.map(e => (
                      <Badge key={e} variant="secondary" className="text-xs">{e}</Badge>
                    ))}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Last delivery: {formatDate(hook.lastDeliveredAt)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={hook.active} onCheckedChange={() => toggleActive(hook)} />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={testingId === hook.id} title="Send a test fire">
                        {testingId === hook.id
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <><PlayCircle className="h-4 w-4 mr-1" /> Test</>}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuLabel className="text-xs text-muted-foreground">
                        Send sample payload for…
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {hook.events.length === 0 && (
                        <DropdownMenuItem disabled>No events subscribed</DropdownMenuItem>
                      )}
                      {hook.events.map(event => (
                        <DropdownMenuItem
                          key={`fire-${event}`}
                          onClick={() => testFire(hook, event)}
                          className="flex items-center justify-between"
                        >
                          <code className="text-xs">{event}</code>
                          <PlayCircle className="h-3 w-3 text-muted-foreground" />
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground">
                        Preview sample JSON…
                      </DropdownMenuLabel>
                      {hook.events.map(event => (
                        <DropdownMenuItem
                          key={`preview-${event}`}
                          onClick={() => openPreview(event)}
                          className="flex items-center justify-between"
                        >
                          <code className="text-xs">{event}</code>
                          <Eye className="h-3 w-3 text-muted-foreground" />
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => testFire(hook, 'webhook.test')}>
                        <Code2 className="h-3 w-3 mr-2" />
                        Send minimal ping (webhook.test)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="outline" size="sm" onClick={() => openEdit(hook)}>Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => rotate(hook)} title="Rotate signing secret">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(hook)} className="text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="px-4 pb-3 flex items-center gap-3 text-xs">
                <button className="text-muted-foreground hover:text-foreground" onClick={() => copy(hook.secret, 'Secret')}>
                  <span className="inline-flex items-center gap-1"><Copy className="h-3 w-3" /> Copy signing secret</span>
                </button>
                <button className="text-muted-foreground hover:text-foreground" onClick={() => toggleExpand(hook.id)}>
                  {expandedId === hook.id ? 'Hide deliveries' : 'Recent deliveries'}
                </button>
              </div>
              {expandedId === hook.id && (
                <div className="border-t bg-muted/30 px-4 py-3">
                  {deliveriesLoading === hook.id ? (
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin mr-2" /> Loading deliveries…
                    </div>
                  ) : !deliveries[hook.id]?.length ? (
                    <div className="text-xs text-muted-foreground">No deliveries yet.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {deliveries[hook.id].map(d => (
                        <div key={d.id} className="text-xs flex items-center gap-3 font-mono">
                          <Badge variant={statusVariant(d.status)} className="text-[10px]">{d.status}</Badge>
                          <span className="w-44 truncate">{d.event}</span>
                          <span className="w-12">{d.responseCode || '—'}</span>
                          <span className="w-8">×{d.attempts}</span>
                          <span className="text-muted-foreground">{formatDate(d.createdAt)}</span>
                          {d.error && <span className="text-destructive truncate">{d.error}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </CardContent>

      <Dialog open={!!previewEvent} onOpenChange={(open) => { if (!open) { setPreviewEvent(null); setPreviewSample(null) } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sample payload — {previewEvent}</DialogTitle>
            <DialogDescription>
              This is the exact shape (with placeholder values) that production traffic will POST.
              Use it to build your n8n / Make.com mappings.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted rounded-md p-3 max-h-96 overflow-auto">
            {previewLoading ? (
              <div className="flex items-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
              </div>
            ) : previewSample ? (
              <pre className="text-xs font-mono whitespace-pre-wrap break-all">
{JSON.stringify({
  version: 1,
  id: '00000000-0000-0000-0000-000000000000',
  event: previewEvent,
  occurred_at: '2026-04-25T15:00:00.000Z',
  organization_id: '00000000-0000-0000-0000-0000000000aa',
  data: { ...previewSample, _test: true },
}, null, 2)}
              </pre>
            ) : (
              <div className="text-sm text-muted-foreground">No sample available.</div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => previewSample && copy(
                JSON.stringify({
                  version: 1,
                  id: '00000000-0000-0000-0000-000000000000',
                  event: previewEvent,
                  occurred_at: '2026-04-25T15:00:00.000Z',
                  organization_id: '00000000-0000-0000-0000-0000000000aa',
                  data: { ...previewSample, _test: true },
                }, null, 2),
                'Sample JSON'
              )}
              disabled={!previewSample}
            >
              <Copy className="h-4 w-4 mr-1" /> Copy JSON
            </Button>
            <Button onClick={() => { setPreviewEvent(null); setPreviewSample(null) }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit webhook' : 'New outbound webhook'}</DialogTitle>
            <DialogDescription>
              POSTs JSON envelopes to this URL whenever a subscribed event fires for this org.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="wh-name">Name</Label>
              <Input
                id="wh-name"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Make.com — leads to HubSpot"
              />
            </div>
            <div>
              <Label htmlFor="wh-url">URL</Label>
              <Input
                id="wh-url"
                value={form.url}
                onChange={e => setForm({ ...form, url: e.target.value })}
                placeholder="https://hook.make.com/..."
              />
            </div>
            <div>
              <Label>Events</Label>
              <div className="grid grid-cols-1 gap-1 mt-1">
                {events.map(event => (
                  <label key={event} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.events.includes(event)}
                      onChange={() => toggleEvent(event)}
                      className="h-4 w-4"
                    />
                    <code className="text-xs">{event}</code>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editing ? 'Save changes' : 'Create webhook'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
